import dayjs from 'dayjs';
import type { FC } from 'react';
import { memo, useEffect, useRef, useState } from 'react';
import { Icon } from '../../icon-svg/pc-icon';
import { ChatInput } from '../components/chat-input';
import { QuestionList } from '../components/chat-list';
import { LiteChatWrapper } from '../components/lite-chat-wrapper';
import { ChatPageSkeleton } from '../components/skeleton';
import { StopButton } from '../components/stop-button';
import { DATE_FORMAT, SIDE_BAR_WIDTH } from '../const';
import { useCheckLimit, useListenerUpgradeAiLimit } from '../hooks/ai-limit-store';
import { useConversationContext } from '../hooks/context';
import { useChatStatus, useConversation, useQuestions } from '../hooks/hook';
import { AI_PAGE_ID, ChatStatus, SideBarType } from '../hooks/types';
import { useChatTranslation } from '../hooks/use-i18n';
import { isMobile } from 'react-device-detect';
import { cx } from '../../cx';
import { isTextTruncatedByRange } from '../hooks/utils';
import { useIsDarkMode } from '../../utils';

export const ChatPage: FC = () => {
  const { conversationId } = useConversationContext();
  const status = useChatStatus(conversationId);
  useListenerUpgradeAiLimit();

  if (status === ChatStatus.LOADING && conversationId === AI_PAGE_ID) {
    return (
      <>
        <ChatPageSkeleton />
        <ChatInputWrapper />
      </>
    );
  }

  return (
    <div className="flex">
      <div className="relative flex flex-col h-screen overflow-hidden w-full text-normal">
        <HeaderBar />
        <Content />
      </div>
      <LiteChatWrapper />
    </div>
  );
};

const HeaderBar: FC = memo(() => {
  const conversation = useConversation();
  const { navigateToChat, navbar, conversationId } = useConversationContext();
  const divRef = useRef<HTMLDivElement>(null);
  /**truncate的时候显示个箭头允许展开 */
  const [titleTruncate, setTitleTruncate] = useState(true);
  const [showArrow, setShowArrow] = useState(false);
  useEffect(() => {
    if (divRef.current) {
      const isTruncate = isTextTruncatedByRange(divRef.current);
      setShowArrow(isTruncate);
    }
  }, [conversationId]);

  return (
    <div className="flex-shrink-0 py-3 bg-white1/50">
      <div className="flex gap-2 mx-auto max-w-6xl px-4">
        <div className="pt-2 flex space-x-2">{navbar?.()}</div>
        {conversation.question && (
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex">
              <div
                ref={divRef}
                className={cx('text-h3 ', isMobile && 'text-h4', {
                  truncate: titleTruncate,
                })}
              >
                {conversation.question}
              </div>
              {showArrow && (
                <Icon
                  onClick={() => {
                    setTitleTruncate(!titleTruncate);
                  }}
                  className="mt-2 animate-click"
                  name={'IcArrowRight01'}
                  size="middle"
                  style={{
                    rotate: titleTruncate ? '0deg' : '90deg',
                  }}
                />
              )}
            </div>
            <div className="text-t4 text-grey3">
              {dayjs(conversation.createdAt).format(DATE_FORMAT)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

const ChatInputWrapper = memo(() => {
  const { sideBarType, conversationId } = useConversationContext();
  const status = useChatStatus(conversationId);
  const isLoading = status === ChatStatus.SENDING;
  const isDarkMode = useIsDarkMode();

  return (
    <>
      {!isDarkMode && (
        <div className="w-full absolute bottom-[124px] left-0 flex justify-between pointer-events-none">
          <div className="px-14 w-full flex items-center justify-center sm:px-5">
            <div className="w-full rounded-lg bg-gradient-to-t from-white to-white/10 relative max-w-2xl">
              <div className={cx('w-full  h-[84px]')} />
            </div>
            <div
              className="flex-shrink-0"
              hidden={sideBarType !== SideBarType.OUTLINE || isMobile}
              style={{ width: SIDE_BAR_WIDTH }}
            />
          </div>
        </div>
      )}
      <div className="w-full absolute bottom-5 left-0 flex justify-between pointer-events-none">
        <div className="px-14 w-full flex items-center justify-center sm:px-5">
          <div
            className="w-full px-4 py-2 rounded-lg bg-white2 pointer-events-auto relative max-w-2xl border border-normal/15 transition-border focus-within:border-normal/35"
            style={{
              boxShadow: '0px 4px 12px 0px #0000000D',
            }}
          >
            <LimitTips />
            {isLoading && <StopButton />}
            <ChatInput />
          </div>
          <div
            className="flex-shrink-0"
            hidden={sideBarType !== SideBarType.OUTLINE || isMobile}
            style={{ width: SIDE_BAR_WIDTH }}
          />
        </div>
      </div>
    </>
  );
});

const LimitTips = () => {
  const { t } = useChatTranslation();
  const { spaceId, openUpgrade } = useConversationContext();
  const checkLimit = useCheckLimit(spaceId);
  if (checkLimit) return null;

  return (
    <div className="text-t4 text-grey3 pb-2 space-x-1">
      <span>{t('ai_quota_exceeded_title')}</span>
      <span className="animate-click text-active_color" onClick={openUpgrade}>
        {t('ai_quota_exceeded_upgrade_button')}
      </span>
    </div>
  );
};

const Content = memo(() => {
  const questions = useQuestions();

  return (
    <>
      <div className="flex flex-col flex-1 h-full mx-auto w-full">
        <QuestionList questions={questions} className="px-14 sm:px-5" />
      </div>
      <ChatInputWrapper />
    </>
  );
});
